import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import { getUserSettings, updateUserSettings } from '../api';
import Tooltip from './Tooltip';

const predefinedClauses = [
    "Lease Term",
    "Rent Amount",
    "Renewal Options",
    "Tenant Name",
    "Landlord Name",
    "Property Address",
    "Security Deposit",
    "Maintenance Responsibilities",
    "Utilities",
    "Pet Policy"
];

export const LeaseAbstractionSettings: React.FC = () => {
    const [selectedPredefinedClauses, setSelectedPredefinedClauses] = useState<string[]>([]);
    const [customClauseInput, setCustomClauseInput] = useState<string>('');
    const [showCustomInput, setShowCustomInput] = useState(false);
    const [savingLeaseClauses, setSavingLeaseClauses] = useState(false);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchLeaseClauses();
    }, []);

    const fetchLeaseClauses = async () => {
        setLoading(true);
        try {
            const settingsData = await getUserSettings();
            const fetchedClauses: string[] = settingsData.lease_abstraction_clauses ?? [];

            const newSelectedPredefined: string[] = [];
            let newCustomInput: string[] = [];

            fetchedClauses.forEach(clause => {
                if (predefinedClauses.includes(clause)) {
                    newSelectedPredefined.push(clause);
                } else {
                    newCustomInput.push(clause);
                }
            });

            setSelectedPredefinedClauses(newSelectedPredefined);
            setCustomClauseInput(newCustomInput.join(', '));
            // Only set showCustomInput to true if there are actual custom clauses
            // Otherwise, let the user control it.
            if (newCustomInput.length > 0) {
                setShowCustomInput(true);
            }

        } catch (error) {
            console.error('Error fetching lease abstraction clauses:', error);
            setMessage('Error fetching lease abstraction clauses.');
        } finally {
            setLoading(false);
        }
    };

    const handlePredefinedClauseChange = (clause: string) => {
        setSelectedPredefinedClauses(prev =>
            prev.includes(clause)
                ? prev.filter(c => c !== clause)
                : [...prev, clause]
        );
    };

    const handleSaveLeaseClauses = async (e: React.FormEvent) => {
        e.preventDefault();
        setSavingLeaseClauses(true);
        setMessage('');
        try {
            let clausesToSave = [...selectedPredefinedClauses];
            if (customClauseInput) {
                const customClausesArray = customClauseInput.split(',').map(clause => clause.trim()).filter(clause => clause.length > 0);
                clausesToSave = [...clausesToSave, ...customClausesArray];
            }
            
            // Remove duplicates
            clausesToSave = Array.from(new Set(clausesToSave));

            await updateUserSettings({ lease_abstraction_clauses: clausesToSave });
            setMessage('Lease abstraction clauses updated successfully!');
            fetchLeaseClauses(); // Re-fetch to ensure the state is in sync with the backend
        } catch (error: any) {
            console.error('Error saving lease abstraction clauses:', error);
            setMessage(error.response?.data?.detail || 'Failed to save lease abstraction clauses.');
        } finally {
            setSavingLeaseClauses(false);
        }
    };

    if (loading) {
        return <div className="text-center p-8 text-text-secondary">{t("leaseAbstractionSettings.k1")}</div>;
    }

    return (
        <div className="container mx-auto p-4 bg-bg-primary">
            <div className="flex items-center mb-6">
                <h1 className="text-3xl font-bold text-text-secondary">{t("leaseAbstractionSettings.k2")}</h1>
                <Tooltip text="Select the specific clauses you want the AI to extract from your lease documents. You can also add custom clauses." />
            </div>

            {message && <p className={`mb-4 text-center p-2 rounded ${message.includes('successful') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{message}</p>}

            <form onSubmit={handleSaveLeaseClauses} className="space-y-4">
                <div className="bg-bg-secondary p-4 rounded shadow border border-border-primary">
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                        {t("leaseAbstractionSettings.k3")} </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {predefinedClauses.map(clause => (
                            <div key={clause} className="flex items-center">
                                <input
                                    type="checkbox"
                                    id={`clause-${clause}`}
                                    checked={selectedPredefinedClauses.includes(clause)}
                                    onChange={() => handlePredefinedClauseChange(clause)}
                                    className="form-checkbox h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary"
                                    disabled={savingLeaseClauses}
                                />
                                <label htmlFor={`clause-${clause}`} className="ml-2 text-text-primary">
                                    {clause}
                                </label>
                            </div>
                        ))}
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="clause-other"
                                checked={showCustomInput}
                                onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    setShowCustomInput(isChecked);
                                    if (!isChecked) {
                                        setCustomClauseInput(''); // Clear custom input if "Other" is unchecked
                                    }
                                }}
                                className="form-checkbox h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary"
                                disabled={savingLeaseClauses}
                            />
                            <label htmlFor="clause-other" className="ml-2 text-text-primary">
                                {t("leaseAbstractionSettings.k4")} </label>
                        </div>
                    </div>
                </div>

                {showCustomInput && (
                    <div className="bg-bg-secondary p-4 rounded shadow border border-border-primary">
                        <label htmlFor="customLeaseClauses" className="block text-sm font-medium text-text-secondary mb-2">
                            {t("leaseAbstractionSettings.k5")} </label>
                        <textarea
                            id="customLeaseClauses"
                            rows={3}
                            className="shadow appearance-none border border-border-primary rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
                            value={customClauseInput}
                            onChange={(e) => setCustomClauseInput(e.target.value)}
                            disabled={savingLeaseClauses}
                            placeholder={t("leaseAbstractionSettings.k6")}
                        ></textarea>
                    </div>
                )}

                <button
                    type="submit"
                    disabled={savingLeaseClauses}
                    className="w-full bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                >
                    {savingLeaseClauses ? 'Saving...' : t("leaseAbstractionSettings.k7")}
                </button>
            </form>
        </div>
    );
};
